import { inject } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';

import { QuestionService } from '@core/ask-hr-dynamic-forms-smart-ui';
import type {
  DefineFormData,
  DynamicFormConfig,
  RespondFormData,
  RespondFormWrapperConfig,
  UnwrapSignal,
} from '@core/ask-hr-dynamic-forms-types';
import type { StringTemplateItem } from '@core/ask-hr-templates-types';
import { I18nService } from '@core/i18n-legacy';

export class TextSingleLineConfig implements DynamicFormConfig {
  private readonly formBuilder = inject(NonNullableFormBuilder);
  private readonly questionService = inject(QuestionService);
  private readonly i18n = inject(I18nService);

  public readonly kind = 'string' satisfies StringTemplateItem['type'];
  public readonly defineConfig: DynamicFormConfig['defineConfig'] = {
    name: this.i18n.translate('tlc.ask_hr.form_template_create.configs.text_single_line'),
    icon: 'short-text',
  };

  public readonly components = {
    define: () => import('@core/ask-hr-dynamic-forms-smart-ui').then(m => m.QuestionComponent),
    respond: () => import('@core/ask-hr-dynamic-forms-smart-ui').then(m => m.RespondTextComponent),
    present: () =>
      import('@core/ask-hr-dynamic-forms-smart-ui').then(m => m.TextPresenterComponent),
  };

  public readonly defineForm = (
    data?: DefineFormData,
  ): UnwrapSignal<import('@core/ask-hr-dynamic-forms-smart-ui').QuestionComponent['defineForm']> =>
    this.questionService.createQuestionForm(data);

  public readonly respondConfig = (
    data: RespondFormWrapperConfig,
  ): UnwrapSignal<
    import('@core/ask-hr-dynamic-forms-smart-ui').RespondTextComponent['respondConfig']
  > => ({
    ...data,
    kind: 'single-line',
    placeholder: this.i18n.translate('tlc.ask_hr.form_template_create.configs.respond_placeholder'),
  });

  public readonly respondForm = (
    config: RespondFormData,
  ): UnwrapSignal<
    import('@core/ask-hr-dynamic-forms-smart-ui').RespondTextComponent['respondForm']
  > =>
    this.formBuilder.group({
      value: this.formBuilder.control<string | null>('', {
        validators: config.required ? Validators.required : [],
      }),
    });

  public readonly presentConfig = (
    data: StringTemplateItem,
  ): UnwrapSignal<
    import('@core/ask-hr-dynamic-forms-smart-ui').TextPresenterComponent['config']
  > => ({
    heading: data.name,
    value: data.value,
    kind: {
      type: 'text',
      multiline: false,
    },
  });
}
